import { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import type { ReportFlow, ReportsV3QueryParams } from '../types';

import { CLEARED_CUSTOM_PARAMS, STANDARD_DEFAULTS } from './useCustomReportOptions';

const flowsSet = new Set<ReportFlow>(['standard', 'custom']);

/**
 * This hook helps to manage the report "flow" (standard vs custom).
 * This is stored in the URL query params, and is used to determine which report options are shown.
 * If there is no flow set (or if its invalid), the effect here will set it.
 *
 * When switching to custom, the URL is seeded with the standard defaults so
 * the user starts with the same options and can adjust from there.
 * When switching back to standard, all custom-specific params are cleared.
 */
export const useReportFlow = (): readonly [ReportFlow, (flow: ReportFlow) => void] => {
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  // Revert to standard flow if the flow is invalid or doesn't exist.
  useEffect(() => {
    if (!query.flow || !flowsSet.has(query.flow)) {
      setQuery({ ...query, ...CLEARED_CUSTOM_PARAMS, flow: 'standard' });
    }
  }, [query, setQuery]);

  const flow: ReportFlow = useMemo(() => {
    if (!query.flow || !flowsSet.has(query.flow)) {
      return 'standard';
    }

    return query.flow;
  }, [query]);

  const setFlow = useCallback((newFlow: ReportFlow) => {
    if (newFlow === 'custom') {
      setQuery({ ...query, ...STANDARD_DEFAULTS, flow: 'custom' });
    } else {
      setQuery({ ...query, ...CLEARED_CUSTOM_PARAMS, flow: 'standard' });
    }
  }, [query, setQuery]);

  return [
    flow,
    setFlow,
  ];
};
